import { apiRequest } from './client';

export type Child = {
  id: string;
  name: string;
  age: number;
  // ISO date string. When set, the backend derives `age` from this live, so
  // the `age` field above is always the kid's current age.
  birthday?: string;
  avatarUrl?: string;
  color?: string;
  parentId: string;
  controls?: Controls;
};

export type Controls = {
  id: string;
  childId: string;
  dailyMinutes: number;
  sessionMinutes: number;
  allowedWindows: { start: string; end: string }[];
  categories: string[];
  interactiveQ: boolean;
  bedtimeLock: boolean;
  autoPlay: boolean;
};

export type Progress = {
  totalWatchedSeconds: number;
  todayWatchedSeconds: number;
  totalSessions: number;
  accuracy: number;
  correctAnswers: number;
  totalQuestions: number;
  favoriteTopics: string[];
};

// Aggregated activity for the parent web Activity tab. Backed by
// /v1/children/:childId/activity. The window is controlled by the `range`
// query param (default "week") — see the Day/Week/Month tabs in the FE.
// `weekly[i]` is indexed Monday..Sunday (FE chart order); for the month
// range each weekday accumulates across the ~4 occurrences in the window.
export type ActivityRange = "day" | "week" | "month";

export type ChildActivity = {
  range: ActivityRange;
  windowDays: number;
  weekly: { dayOfWeek: number; secondsWatched: number }[];
  categories: {
    name: string;
    secondsWatched: number;
    pct: number;
  }[];
  recent: {
    at: string;
    videoTitle: string;
    minutes: number;
    stars: number;
  }[];
  streakDays: number;
  weekMinutes: number;
};

export const childrenApi = {
  list: (token: string) =>
    apiRequest<Child[]>('/v1/children', { token }),

  create: (
    data: {
      name: string;
      age: number;
      birthday?: string;
      avatarUrl?: string;
      color?: string;
    },
    token: string,
  ) => apiRequest<Child>('/v1/children', { method: 'POST', body: data, token }),

  update: (
    id: string,
    data: Partial<{
      name: string;
      age: number;
      birthday: string;
      avatarUrl: string;
      color: string;
    }>,
    token: string,
  ) =>
    apiRequest<Child>(`/v1/children/${id}`, { method: 'PATCH', body: data, token }),

  remove: (id: string, token: string) =>
    apiRequest<{ ok: boolean }>(`/v1/children/${id}`, { method: 'DELETE', token }),

  getControls: (childId: string, token: string) =>
    apiRequest<Controls>(`/v1/children/${childId}/controls`, { token }),

  updateControls: (childId: string, data: Partial<Controls>, token: string) =>
    apiRequest<Controls>(`/v1/children/${childId}/controls`, {
      method: 'PUT',
      body: data,
      token,
    }),

  getProgress: (childId: string, token: string) =>
    apiRequest<Progress>(`/v1/children/${childId}/progress`, { token }),

  getActivity: (
    childId: string,
    token: string,
    range: ActivityRange = "week",
  ) =>
    apiRequest<ChildActivity>(
      `/v1/children/${childId}/activity?range=${range}`,
      { token },
    ),

  getRecommendations: (childId: string, token: string) =>
    apiRequest<Video[]>(`/v1/children/${childId}/recommendations`, { token }),

  getContinueWatching: (childId: string, token: string) =>
    apiRequest<Video[]>(`/v1/children/${childId}/continue-watching`, { token }),
};

export type Video = {
  id: string;
  title: string;
  thumbUrl: string;
  duration: number;
  categories: string[];
  isPremium: boolean;
};
